import { useEffect, useRef, useState } from 'react'
import { useInView } from 'framer-motion'
import { cn } from '@/lib/utils'

interface ProgressBarProps {
  /** 현재 값 (0~max) */
  value: number
  /** 최대 값 */
  max: number
  /** 라벨 텍스트 */
  label?: string
  /** 우측에 표시할 값 텍스트 */
  valueText?: string
  /** 바 색상 */
  color?: 'primary' | 'secondary' | 'accent' | 'grade'
  /** 바 높이 */
  size?: 'sm' | 'md' | 'lg'
  /** 추가 CSS 클래스 */
  className?: string
}

const colorStyles = {
  primary: 'bg-gradient-to-r from-cb-primary to-blue-400',
  secondary: 'bg-gradient-to-r from-cb-secondary to-emerald-400',
  accent: 'bg-gradient-to-r from-cb-accent to-amber-400',
  grade: '', // 동적으로 결정
} as const

const sizeStyles = {
  sm: 'h-1.5',
  md: 'h-3',
  lg: 'h-4',
} as const

/** 점수에 따른 색상 결정 (등급 기반 그라데이션) */
function getGradeColor(percentage: number): string {
  if (percentage >= 90) return 'bg-gradient-to-r from-emerald-400 to-teal-500'
  if (percentage >= 75) return 'bg-gradient-to-r from-blue-400 to-indigo-500'
  if (percentage >= 60) return 'bg-gradient-to-r from-amber-400 to-yellow-500'
  if (percentage >= 40) return 'bg-gradient-to-r from-orange-400 to-amber-500'
  return 'bg-gradient-to-r from-red-400 to-orange-500'
}

/**
 * 프로그레스 바 컴포넌트.
 *
 * - 적합도 %, 평가 점수, 정원 현황 등에 사용
 * - 뷰포트 진입 시 0%에서 목표%까지 애니메이션
 * - 접근성: role="progressbar", aria-valuenow 등
 * - 등급별 자동 색상 (grade 모드)
 * - prefers-reduced-motion 대응
 */
export default function ProgressBar({
  value,
  max,
  label,
  valueText,
  color = 'primary',
  size = 'md',
  className,
}: ProgressBarProps) {
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-20px' })
  const [animatedWidth, setAnimatedWidth] = useState(0)

  const percentage = max > 0 ? Math.min(Math.round((value / max) * 100), 100) : 0
  const barColor = color === 'grade' ? getGradeColor(percentage) : colorStyles[color]

  useEffect(() => {
    if (!isInView) return

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReducedMotion) {
      setAnimatedWidth(percentage)
      return
    }

    // 짧은 딜레이 후 애니메이션 시작 (mount 후 transition이 걸리도록)
    const timer = requestAnimationFrame(() => {
      setAnimatedWidth(percentage)
    })
    return () => cancelAnimationFrame(timer)
  }, [isInView, percentage])

  return (
    <div ref={ref} className={cn('flex flex-col gap-1.5', className)}>
      {(label || valueText) && (
        <div className="flex items-center justify-between">
          {label && (
            <span className="text-body-sm font-medium text-cb-gray-600">{label}</span>
          )}
          {valueText && (
            <span className="text-body-sm font-semibold text-cb-gray-900 text-amount">
              {valueText}
            </span>
          )}
        </div>
      )}
      <div
        className={cn('w-full overflow-hidden rounded-[6px] bg-cb-gray-100', sizeStyles[size])}
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-label={label ?? `${percentage}%`}
      >
        <div
          className={cn(
            'h-full rounded-[6px] transition-[width] duration-700 ease-out',
            barColor,
          )}
          style={{ width: `${animatedWidth}%` }}
        />
      </div>
    </div>
  )
}
